import React, { useCallback, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import ComplexVariablesList from './complexVariables/ComplexVariablesList';
import { BusinessLogic } from '../../context/businessLogicContext';
import { VARIABLE_OBJECT_TYPES, VARIABLE_TYPE } from '../../utils/constants/appConstants';
import {
  CONTEXT_KEYS,
  TEMPLATE_TYPE,
  useArtboardContext,
} from '../../context/complexArtboardContext';
import {
  STATIC_TABLE_BLOCK,
} from '../../utils/complexDocumentConstants';
import { getLocalVariable, mergeLocalVariable, getStoreStaticTableBlock } from './complexDocumentHelper';
import { Tabs } from 'antd';
import { ComplexPreviewTabsContainer } from './styles';
import { ComplexVariableOverrideList } from './complexVariables/ComplexVariableOverrideList';
import { useTranslation } from 'react-i18next';
import { COMPLEX_VARIABLES_SIDEBAR } from './complexVariables/translationValues';
import { useSessionContext } from '../../context/sessionContext';
import { checkUserAccessRoles } from '../../utils/helper';
import { useEmbedding } from '../../context/embedContext';

const { TabPane } = Tabs;

const EMPTY_LIST = [];
const NAMESPACES = ['COMPLEX_VARIABLES_SIDEBAR'];
const PRIMITIVE_VARIABLE_TYPES = ['string', 'String', 'Date', 'date'];

const ComplexDocumentVariableList = ({ visible, editable,setIsHtmlExpanded, isHtmlExpanded}) => {
  const { t } = useTranslation(NAMESPACES);
  const { embedState } = useEmbedding();
  const variablesSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [allVariables] = useDocumentContext(variablesSelector);
  const contextSelector = useCallback(store => store, []);
  const [documentContext] = useDocumentContext(contextSelector);
  const { businessLogics } = useContext(BusinessLogic);

  const fetchingVariablesSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES], []);
  const [variablesLoading, method] = useDocumentContext(fetchingVariablesSelector);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [ currentDesign, updateArtboard] = useArtboardContext(getCurrentDesign);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexDocumentName`);

  const isOpenDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `ComplexDocumentName`);

  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [documentDataSet] = useDocumentContext(dataSetSelector);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const customSelector = useCallback(
    store => {
      let variablesList = [];
      if (variablesLoading) {
        return EMPTY_LIST;
      }
      const allFlows = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
      const allBlocks = allFlows.reduce((acc, flowUUID) => {
        const flow = store[flowUUID];
        const flowBlocks = Object.keys(flow)
          .filter(key => key.startsWith(`${flowUUID}-`))
          .reduce((blockAcc, pageKey) => {
            return [...blockAcc, ...flow[pageKey].reduce((list, b) => {
               if (store[b].blockType === 'parent') {
                if (!!store[b]?.groupObject) {
                  return [...list, b];
                } else {
                  return [...list, ...store[b].origContents];
                }
               }
               return [...list, b];
             }, [])];
          }, []);
        const staticBlocks = [];
        if (flow.header) {
          staticBlocks.push(flow.header);
        }
        if (flow.footer) {
          staticBlocks.push(flow.footer);
        }
        return [...acc, ...flowBlocks, ...staticBlocks];
      }, []);
      allBlocks.forEach(blockUUID => {
        const block = store[blockUUID];
        let d = block.variableContract?.forEach(blockVariable => {
          const variableIndex = variablesList.findIndex(item => {
            return (
              (item.placeHolderText &&
                blockVariable.name &&
                item.placeHolderText === blockVariable.name) ||
              (item.id && blockVariable.variableId && item.id === blockVariable.variableId)
            );
          });
          if (variableIndex === -1) {
            const variableDetails = allVariables.find(gVar => gVar.id === blockVariable.variableId);
            if (variableDetails) {
              variablesList.push(variableDetails);
            } else if (blockVariable.type) {
              const tempBlock = getStoreStaticTableBlock(block, store);
              // Probably a local variable
              const localVariable = getLocalVariable(blockVariable, tempBlock, allVariables);
              variablesList.push(localVariable);
            }
          } else {
            let variableDetails = variablesList[variableIndex];
            variableDetails = mergeLocalVariable(
              variableDetails,
              blockVariable,
              allVariables,
              block
            );
            variablesList.splice(variableIndex, 1, variableDetails);
          }
        });
      });
      const templateInserts = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS];
      const templateBusinessLogics = templateInserts?.map(({businessLogic}) => businessLogic).filter(v => !!v) || [];
      const allBusinessLogics = [...(businessLogics?.length ? businessLogics : []), ...templateBusinessLogics];
      if (allBusinessLogics?.length > 0) {
        allBusinessLogics.forEach(logic => {
          if (logic?.variables?.length > 0) {
            logic.variables.forEach(variable => {
              const variableDetails = allVariables.find(gVar => gVar.id === variable.variableId);
              if (!variableDetails) return;

              if (variable.objectName) {
                const objectIndex = variablesList.findIndex(item => item.name === variable.objectName);
                if (objectIndex !== -1 && Array.isArray(variablesList[objectIndex].variables)) {
                  if (variablesList[objectIndex].variables.find(v => v.id === variable.variableId)) {
                    return;
                  }
                  variablesList[objectIndex].variables.push(variableDetails);
                  return;
                }
              }

              const alreadyExists = variablesList.some(item => item.id === variable.variableId);
              if (!alreadyExists) {
                variablesList.push(variableDetails);
              }
            });
          }
        });
      }

      const updateVariableList = (vars) => {
        const listOfVars = allVariables.filter(item => vars.some(v => v.variableId === item.id));
        listOfVars.forEach(item => {
          const exists = variablesList.some(existingItem => existingItem.id === item.id);
          if(!exists)
            variablesList.push(item);
        });
      }
      const documentSummary = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY];
      const displayName = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME];
      const summaryVariables = documentSummary?.variableContract;
      const displayNameVariables = displayName?.variableContract;
      if (summaryVariables) {
        updateVariableList(summaryVariables);
      }
      if (displayNameVariables) {
        updateVariableList(displayNameVariables);
      }
      return variablesList;
    },
    [allVariables, variablesLoading, businessLogics],
  );

  const [variablesList] = useDocumentContext(customSelector, 'ComplexDocumentVariableList');

  useEffect(() => {
    if(!visible){
        updateArtboard({ [CONTEXT_KEYS.IS_PREVIEW]: false });
    }
  }, [visible])

  /*useEffect(() => {
    console.log("Change in document Variables :: ", variablesList);
    const localVariables = [];
    let hasChanges = false;
    let tableName = undefined;

    const calculateLocalVariables = (list, parentName = undefined) => {
      for (let i = 0; i < list.length; i++) {
        let contract = list[i];
        if (parentName === undefined) {
          tableName = contract.placeHolderText;
        }
        if (contract.id !== undefined) {
          continue;
        } else {
          if (contract.type === 'tableObject') {
            calculateLocalVariables(contract.variables, contract.name);
          } else if (contract.type === 'object') {
            let tableContract = contract.variables.find(c => c.type === 'tableObject');
            if (tableContract) {
              tableName = tableContract.placeHolderText;
              calculateLocalVariables(tableContract.variables, tableContract.placeHolderText);
            }
          } else if (PRIMITIVE_VARIABLE_TYPES.includes(contract.type) && contract.id === undefined && parentName !== undefined) {
              let variableCategoryName = parentName.replaceAll(`${tableName}.`, '').split('.')[0];
              let placeHolderText = contract.name.replace(`${tableName}.`, '');
              if (localVariables.find(lv => lv.placeHolderText === placeHolderText) === undefined) {
                hasChanges = true;
                localVariables.push({
                  name: contract.placeHolderText,
                  placeHolderText,
                  type: contract.type,
                  variableCategoryId: 0,
                  variableCategoryName
                });
              }
          }
        }
      }
    }

    calculateLocalVariables(variablesList);
    if (hasChanges) {
      method.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES]: localVariables
      }, [], true);
      console.log("local variables set :: ", localVariables, method.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES])
    }
  }, [variablesList]);*/
  
  return (
    <>
      {visible && (
        <ComplexPreviewTabsContainer>
          <Tabs tabBarStyle={{ color: 'white' }}>
            <TabPane
              tab={
                isInteractiveDesign || checkUserAccessRoles(userDetails, ["DESIGNER", "PUBLISHER"])
                  ? t(
                      COMPLEX_VARIABLES_SIDEBAR.DOWNLOAD_AND_PREVIEW_LABEL.key,
                      COMPLEX_VARIABLES_SIDEBAR.DOWNLOAD_AND_PREVIEW_LABEL.value
                    )
                  : t(
                      COMPLEX_VARIABLES_SIDEBAR.PREVIEW_LABEL.key,
                      COMPLEX_VARIABLES_SIDEBAR.PREVIEW_LABEL.value
                    )
              }
              key="1"
            >
              <ComplexVariablesList
                documentVariables={variablesList}
                globalVariables={allVariables}
                businessLogics={businessLogics}
                isHtmlExpanded={isHtmlExpanded}
                setIsHtmlExpanded={setIsHtmlExpanded}
              />
            </TabPane>
            {!isOpenDesign && currentDesign === TEMPLATE_TYPE.DOCUMENT ? (
              <TabPane
                tab={t(
                  COMPLEX_VARIABLES_SIDEBAR.OVERRIDE_VARIABLES_LABEL.key,
                  COMPLEX_VARIABLES_SIDEBAR.OVERRIDE_VARIABLES_LABEL.value,
                )}
                key="2"
              >
                <ComplexVariableOverrideList variablesList={variablesList} editable={editable}/>
              </TabPane>
            ) : null}
          </Tabs>
        </ComplexPreviewTabsContainer>
      )}
      {embedState.hideDesignSidebar && documentDataSet ? (
        // When sidebar is hidden in embed mode, still render ComplexVariablesList (hidden) so event listeners remain active for iframe communication
        <div style={{ display: 'none' }}>
          <ComplexVariablesList
            documentVariables={variablesList}
            globalVariables={allVariables}
            businessLogics={businessLogics}
            isHtmlExpanded={isHtmlExpanded}
            setIsHtmlExpanded={setIsHtmlExpanded}
          />
        </div>
      ) : null}
      {document.getElementById('document-variable-counter') &&
        ReactDOM.createPortal(
          <span className="docVarCount">{variablesList.length}</span>,
          document.getElementById('document-variable-counter'),
        )}
    </>
  );
};

export default ComplexDocumentVariableList;
